import { useEffect, useImperativeHandle, useRef, type ReactNode, type Ref } from "react";
import { Link } from "@tanstack/react-router";
import { useT } from "../i18n";
import { Zeichen, type Zeichenname } from "./Zeichen";
import "../stile/handy.css";

/* ══════════════════════════════════════════════════════════════════
   Die Formen der Handyfassung — zwölf Bausteine, mehr gibt es nicht.

   Luca, 18.09.2026, nach dem Durchsehen der Handyfassung von OVIS:

   > *„Ich möchte, dass du dir viel abguckst von dem Design und generell auch
   > das Design ein wenig optimierst. … nicht nur, dass du die Header-Leiste
   > überarbeitest, sondern generell die Masken anpasst. Du sollst dir so ein
   > bisschen dieses aufgeräumte Design vom OVIS Mobile Interface abgucken und
   > entsprechend adaptieren auf Machine Office, sodass wir da auch ein
   > bisschen moderner werden vom Style."*

   ── Warum das Bausteine sind und keine Beschreibung ────────────────

   **Weil eine Beschreibung auseinanderläuft.** Das ist im Haus schon einmal
   passiert und steht aufgeschrieben: Bis zum 15.09.2026 gab es drei
   Listenformen nebeneinander, mit drei Zeilenhöhen und drei Kopfzeilen, und
   die Spaltenbreiten standen als `style`-Attribut an jeder einzelnen Zeile
   jeder Maske (28-designkonzept.md, „Die Tabelle ist ein Baustein, kein
   Muster"). Dieselbe Gefahr besteht hier: Fünf Masken, die alle „eine Karte
   mit Zeilen" bauen, bauen fünf verschiedene.

   > **Gleiche Struktur, nicht gleiches Aussehen um jeden Preis.** Was eine
   > Maske besonders macht, steckt in ihrem **Inhalt**. Was alle teilen,
   > steckt hier.

   ── Das Vorbild und was davon nicht mitkommt ───────────────────────

   Der Aufbau ist von OVIS abgesehen (42-vorbild-ovis.md, Teil 2): große
   Seitenüberschrift, Abschnittsüberschrift mit der Auskunft rechts daneben,
   **eine** Karte je Abschnitt mit eingerückten Trennlinien darin, zweizeilige
   Zeilen, Leerzustände als Satz, und am Ende eines kurzen Überblicks der Weg
   zum Rest.

   **Die Farben und Schriften sind unsere** (28-designkonzept.md): Hausgelb
   nur als Fläche, Saira für Überschriften, Saira Condensed für kurze
   Versalien, Archivo für alles, was ein Satz ist, JetBrains Mono für alles,
   was verglichen wird. **Übernommen ist Aufbau und Verhalten, nie das
   Aussehen.**

   ── Jede Klasse trägt `hf-` ───────────────────────────────────────

   *Nachgezogen am Abend des 18.09.2026, nachdem es schiefgegangen war.*

   Die erste Fassung hieß `.abschnitt`, `.zeilenblatt`, `.seitenkopf` — kurz
   und im Ton des Hauses. **`.abschnitt-kopf` gab es schon**, seit dem Bau des
   Bretts: die klebende Gruppenzeile einer Ressourcengruppe (`stile/brett.css`)
   — graue Fläche, Kante darunter, Versalien mit `.16em`, `position: sticky`,
   `cursor: pointer`. Und `brett.css` hängt in `main.tsx`, ist also **auf jedem
   Schirm geladen**, auch dort, wo kein Brett steht.

   Das Ergebnis war auf 390 × 844 zu sehen: Jede Abschnittsüberschrift stand
   als graues Band über die volle Breite, klebte beim Rollen oben fest, und aus
   „1 raus · 1 rein" wurde „1 RAUS · 1 REIN". Nicht nur auf einem Schirm —
   überall, wo `Abschnitt` steht.

   > **Deshalb trägt jede Klasse dieser Datei den Vorsatz `hf-`.** Nicht nur
   > die eine, die kollidiert ist: Ein Vorsatz für die Familie verhindert die
   > **nächste** Kollision, ein umbenannter Einzelfall nur die eine.

   Die Bausteine heißen unverändert `Seitenkopf`, `Abschnitt`, `Zeilenblatt` —
   der Vorsatz ist eine Sache der Klassennamen, nicht der Aufrufer. Wer hier
   eine neue Form ergänzt, nennt sie `hf-…` und prüft den Namen mit
   `grep -rnE "\.name\b" apps/web/src/stile packages/ui/src` gegen **alle**
   Stile — nicht nur gegen die, die man im Sinn hat. (`grep "\bname"` allein
   reicht nicht: `\b` trifft im macOS-grep nicht zuverlässig, und genau daran
   ist die erste Prüfung vorbeigelaufen.)

   ── Wo sie gelten ─────────────────────────────────────────────────

   Die Formen sind für die **Handyfassung** gebaut und tragen dort ihre Maße
   (`stile/handy.css`). Am Schreibtisch funktionieren sie, sind aber selten
   richtig: Dort gibt es die Tabelle (`bausteine/Tabelle.tsx`) und den Bogen,
   und eine Karte mit fünf Zeilen neben einer Tabelle mit sechsunddreißig
   sieht aus wie ein Fehler.
   ══════════════════════════════════════════════════════════════════ */

/* ══════════════ 1b · Der Aktenkopf (19.09.2026) ══════════════

   **Nehmen, wenn** ein einzelner Datensatz am Telefon seinen Namen groß
   tragen soll und dazu einen Rückweg braucht — ein Einsatz, ein Kunde, ein
   Angebot. Der Rückpfeil steht links und hängt an der **ersten** Zeile des
   Namens: Bei einem zweizeiligen Namen säße ein mittig gesetzter Pfeil
   zwischen den Zeilen (so auch das Vorbild an `.tf-navbar--gross`).

   **Nicht nehmen, wenn** die Maske eine Wurzel ist (Start, Suche) — die hat
   keinen Rückweg und nimmt den `Seitenkopf` allein.

   Gebaut war er zuerst im Einsatzschirm (`.ez-kopf`, 18.09.2026). Luca am
   19.09.2026 über das Angebot: *„Du hast hier nicht das schöne Design mit
   Angebot und mit der großen Überschrift."* — also gehört er allen und
   nicht einer Maske. `zurueck` ist das fertige Element (`<Zurueck>` oder
   `<ZurueckKnopf>` aus `schale/Kopf.tsx`), weil die beiden verschieden
   zurückgehen: über die Adresse oder über einen Schritt im Blatt. */
export function Aktenkopf({ zurueck, titel, unter, rechts }: {
  zurueck: ReactNode;
  titel: ReactNode;
  unter?: ReactNode;
  rechts?: ReactNode;
}) {
  return (
    <div className="hf-aktenkopf">
      {zurueck}
      <Seitenkopf titel={titel} {...(unter ? { unter } : {})} {...(rechts ? { rechts } : {})} />
    </div>
  );
}

/* ══════════════ 1 · Der Seitenkopf ══════════════

   **Nehmen, wenn** eine Maske am Telefon ihren Namen selbst tragen soll —
   dann gibt sie keinen `<Kopf>` aus, das Portalziel bleibt leer und zeichnet
   nichts (`stile/telefon.css`), und die Maske gewinnt rund 90 px.

   **Nicht nehmen, wenn** die Maske einen Rückweg, einen Zusammenhang
   (Nummer, Marke, Umschalter) oder eine Hauptaktion oben braucht. Das ist der
   Maskenkopf, und der bleibt `<Kopf>` — sonst steht der Rückweg zweimal auf
   dem Schirm, einmal oben und einmal im Inhalt.

   **Der Untertitel ist eine Einordnung, keine Wiederholung.** „Freitag,
   18. September" unter „Start" sagt etwas; „Die Startseite" unter „Start"
   sagt nichts. */
export function Seitenkopf({ titel, unter, rechts }: {
  titel: ReactNode;
  /** Die leise Zeile darunter — Datum, Ort, Zahl. Nicht der Titel noch einmal. */
  unter?: ReactNode;
  /** Eine einzelne Handlung rechts auf der Titelzeile, etwa ein Kreuz zum Schließen. */
  rechts?: ReactNode;
}) {
  return (
    <header className="hf-seitenkopf">
      <div className="hf-seitenkopf-zeile">
        <h1>{titel}</h1>
        {rechts ? <div className="hf-seitenkopf-rechts">{rechts}</div> : null}
      </div>
      {unter ? <p className="hf-seitenkopf-unter">{unter}</p> : null}
    </header>
  );
}

/* ══════════════ 2 · Der Abschnitt ══════════════

   Überschrift links, **die Auskunft dazu rechts auf derselben Zeile** —
   „Zuletzt · 3 von 21", „Was ist offen? · 1". Leiser Text, keine Marke: Eine
   Marke ist ein Zustand (28-designkonzept.md), und „3 von 21" ist keiner.

   **Nehmen, wenn** ein Block Inhalt eine Überschrift braucht. Das ist der
   Regelfall; alles andere hier steht **in** einem Abschnitt.

   **Nicht nehmen für einen einzelnen Wert.** Eine Überschrift über einer
   Zeile ist eine Gruppe um ein einzelnes Ding.

   **Die Auskunft steht nur da, wenn sie etwas sagt.** „Zuletzt · 3 von 21"
   sagt, dass es mehr gibt; „Zuletzt · 3 von 3" sagt nichts und bleibt weg.
   Das entscheidet die Maske, nicht dieser Baustein — er zeichnet nur, was er
   bekommt. */
export function Abschnitt({ titel, auskunft, weg, params, beiWeg, wegWort, children }: {
  titel: ReactNode;
  /** Der leise Text rechts — eine Zahl, eine Menge, ein Stand. */
  auskunft?: ReactNode;
  /** Ein Weg zur vollen Liste. Steht ganz rechts, hinter der Auskunft. */
  weg?: string;
  /** Die Platzhalter darin — `/kunden/$nr` braucht `{ nr }`. Siehe „Ein Ziel ist nicht immer eine Adresse". */
  params?: Record<string, string>;
  /** Statt eines Wegs: ein Reiter, ein Blatt, ein Rollsprung. */
  beiWeg?: () => void;
  /** Sein Wort — „alle", „alles anzeigen". Ohne Weg wirkungslos. */
  wegWort?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="hf-abschnitt">
      <div className="hf-abschnitt-kopf">
        <h2>{titel}</h2>
        {auskunft ? <span className="hf-abschnitt-auskunft">{auskunft}</span> : null}
        <Ziel weg={weg} params={params} beiKlick={beiWeg} klasse="hf-abschnitt-weg">
          {wegWort}<Zeichen name="weiter" />
        </Ziel>
      </div>
      {children}
    </section>
  );
}

/* ══════════════ Ein Ziel ist nicht immer eine Adresse ══════════════

   *Nachgezogen am Abend des 18.09.2026, als die Kundenakte die erste Maske
   war, die alle acht Formen zugleich benutzt.*

   Die erste Fassung nahm überall nur `zu: string`. Zwei Dinge gingen damit
   nicht, und beide sind der Regelfall und nicht die Ausnahme:

   1. **`/kunden/$nr` braucht seine Platzhalter.** Ohne `params` kommt der
      Verweis dort gar nicht an.
   2. **Der „Rest" ist nicht immer woanders.** In der Kundenakte ist die volle
      Fassung ein **Reiter** — Zustand in `Kunde.tsx`, keine zweite Adresse.
      Ein Baustein, der nur Adressen kennt, zwingt die Maske, eine zu
      erfinden.

   Deshalb nehmen `Abschnitt` und `Weiterweg` beides: `weg` (+ `params`) für
   eine Adresse, `beiKlick`/`beiWeg` für alles andere. **Beides zugleich ist
   ein Weg zu viel** — dann gewinnt die Adresse, und das ist eine Warnung
   wert, keine Eleganz.

   Gezeichnet wird in beiden Fällen dasselbe: Wer es sieht, soll nicht
   erraten müssen, ob dahinter eine Adresse steht. */
function Ziel({ weg, params, beiKlick, klasse, children }: {
  weg?: string | undefined;
  params?: Record<string, string> | undefined;
  beiKlick?: (() => void) | undefined;
  klasse: string;
  children: ReactNode;
}) {
  if (weg) {
    return <Link to={weg} params={params as never} className={klasse}>{children}</Link>;
  }
  if (beiKlick) {
    return <button type="button" className={klasse} onClick={beiKlick}>{children}</button>;
  }
  return null;
}

/* ══════════════ 3 · Das Zeilenblatt ══════════════

   **Eine Karte je Abschnitt, nicht eine je Zeile.** Das ist der sichtbarste
   Unterschied zum heutigen Stand und der Grund, warum das Vorbild aufgeräumt
   wirkt: Fünf Karten untereinander sind fünf Kanten, fünf Schatten und vier
   Lücken; eine Karte mit fünf Zeilen ist eine Fläche mit vier Haarlinien.

   **Die Linien sind eingerückt** und laufen nicht bis an den Kartenrand. Eine
   Linie, die die Karte durchschneidet, teilt sie in zwei Karten.

   **Nehmen, wenn** mehrere gleichartige Dinge untereinanderstehen — Zeilen,
   Wege, Werte.

   **Nicht nehmen für einen einzelnen Satz** (dafür gibt es `Leersatz` und
   `Kurzfassung`) und **nicht für Dinge, die nicht gleichartig sind**: Eine
   Karte, in der eine Zeile, ein Foto und ein Formular untereinanderstehen,
   ist kein Blatt, sondern ein Kasten. */
export function Zeilenblatt({ children }: { children: ReactNode }) {
  return <div className="hf-zeilenblatt">{children}</div>;
}

/** Der Punkt links an einer Zeile. Siehe `Zweizeiler`. */
export type Punkt = "ruhig" | "gut" | "acht" | "stopp";

/* ══════════════ 4 · Der Zweizeiler ══════════════

   Die Listenzeile der Handyfassung: **Punkt links, Titel, darunter leise die
   Einordnung, rechts die Zeit oder der Winkel.** 56 px hoch, zwei Zeilen.

   **Der Punkt trägt Form und nicht nur Farbe** (28-designkonzept.md, „Farbe
   ist nie der einzige Träger"): `gut` und `stopp` sind gefüllt, `acht` ist
   ein **offener Ring** in abgedunkeltem Gelb — „was sich von selbst auflöst,
   ist der offene Ring; was liegen bleibt, bis ein Mensch etwas tut, ist der
   rote Punkt". `ruhig` ist gar kein Punkt und nimmt auch keinen Platz weg.

   **Nehmen, wenn** eine Zeile auf etwas zeigt, das man öffnen kann — einen
   Vorgang, einen Kunden, einen Einsatz, einen Weg.

   **Nicht nehmen für ein Wertepaar.** „Betriebsstunden 1 240" ist keine Zeile
   mit Ziel, sondern eine Angabe; die steht in einem `Zeilenblatt` als
   schlichte Zeile, ohne Winkel und ohne Klick. Ein Winkel, der nirgendwohin
   führt, ist ein Versprechen.

   **Und nicht als zweiter Weg zu derselben Sache**: Wo die Zeile öffnet,
   steht kein Knopf „Ändern" daneben (28-designkonzept.md, „Die Zeile öffnet
   den Datensatz").

   ── Ein Merkmal bricht nicht um, ein Satz schon ────────────────────

   *Nachgezogen am Abend des 18.09.2026.* Die erste Fassung schnitt beide
   Zeilen hart ab. Für den Titel ist das richtig und bleibt: Er ist ein
   **Name** — eine Kennung, eine Firma, ein Gerät —, und ein umbrechender
   Name reißt die Höhe der ganzen Liste auf.

   Für die zweite Zeile stimmte es nur zur Hälfte. Sie trägt zweierlei:

   | | Beispiel | |
   |---|---|---|
   | ein **Merkmal** | „Vermietung · Minibagger MB-15" | bleibt einzeilig, abgeschnitten |
   | ein **Satz** | „Rücknahme ist da — die Freigabe fehlt" | `satz`, bricht auf **zwei** Zeilen um |

   > **Ein Merkmal ist so lang, wie die Spalte breit ist. Ein Satz ist so
   > lang, wie er ist.** Wer „Rücknahme ist da — die Freiga…" liest, hat
   > nichts erfahren; das ist dieselbe Beobachtung wie in
   > 28-designkonzept.md („Gestapelt wird nichts abgeschnitten").

   **Zwei Zeilen und nicht unbegrenzt:** Die Liste bleibt eine Liste. Wer
   mehr braucht, hat keinen Satz mehr, sondern einen Text — und der gehört
   in eine `Kurzfassung` oder hinter die Zeile, nicht in sie hinein.

   **Eine diktierte Notiz gehört nicht in den Titel.** Sie ist ein Satz, also
   steht sie in `unter` mit `satz`; in den Titel gehört, woran man sie
   wiedererkennt (der Kunde, das Datum). */
export function Zweizeiler({
  zu, params, beiKlick, punkt = "ruhig", titel, unter, satz = false, rechts, winkel = true, gedrueckt,
  ohnePunkt = false,
}: {
  /**
   * Ohne Punktspalte (Signal, Paket 5): Wo die Angabe rechts den Zustand
   * schon mit Form und Wort trägt (`Zustandsmarke`), wäre der Punkt ein
   * zweiter Träger derselben Aussage — und seine Spalte kostet den Namen
   * 21 px. Dann gilt die Liste als Ganzes ohne Spalte, nicht einzelne Zeilen.
   */
  ohnePunkt?: boolean;
  /** Das Ziel. Entweder `zu` **oder** `beiKlick` — beides zugleich ist ein Weg zu viel. */
  zu?: string;
  params?: Record<string, string>;
  beiKlick?: () => void;
  punkt?: Punkt;
  titel: ReactNode;
  /** Die leise Zeile darunter: Art · Zusammenhang. Mit `·` getrennt, nicht mit Kommas. */
  unter?: ReactNode;
  /**
   * **Ist die zweite Zeile ein Satz?** Dann darf sie auf zwei Zeilen
   * umbrechen; siehe „Ein Merkmal bricht nicht um, ein Satz schon".
   */
  satz?: boolean;
  /** Rechts: eine Zeit, eine Zahl, eine Marke. */
  rechts?: ReactNode;
  /** Der Winkel rechts. Steht nur, wo die Zeile wirklich öffnet. */
  winkel?: boolean;
  /**
   * **Die Zeile ist ein Schalter** (20.09.2026): Ein Tipp setzt etwas, der
   * nächste nimmt es zurück — die Bestätigung im Abschluss der Sammlung. Der
   * Haken rechts trägt es für das Auge, `aria-pressed` für den Vorleser;
   * ohne beides wäre der Zustand nur eine Farbe. Nur mit `beiKlick`.
   */
  gedrueckt?: boolean;
}) {
  const inhalt = (
    <>
      {/* **Der Punkt steht immer da, auch wenn er nichts zeigt.** Ließe man
          ihn bei `ruhig` weg, begänne die Zeile 21 px weiter links als ihre
          Nachbarin — in einer gemischten Liste („Was ist offen?") zackt die
          linke Kante dann, und das liest sich als Versehen. Das Vorbild hält
          die Spalte ebenso unbedingt frei (`--tfm-punktspalte`, und dort
          steht eigens: „seine Zelle ist breiter als er selbst"). */}
      {ohnePunkt ? null : <span className="hf-zweizeiler-punkt" data-punkt={punkt} aria-hidden="true" />}
      <span className="hf-zweizeiler-text">
        <b>{titel}</b>
        {unter ? <small>{unter}</small> : null}
      </span>
      {rechts ? <span className="hf-zweizeiler-rechts">{rechts}</span> : null}
      {winkel && (zu || beiKlick) ? <Zeichen name="weiter" /> : null}
    </>
  );
  const eigenschaften = {
    className: "hf-zweizeiler",
    "data-punkt": punkt,
    ...(ohnePunkt ? { "data-ohne-punkt": "ja" } : {}),
    ...(satz ? { "data-satz": "ja" } : {}),
  };
  /* Ein Ziel ist ein Verweis, eine Handlung ist ein Knopf, und ohne beides
     ist es Text. Drei Fälle, damit die Tastatur und der Vorleser dasselbe
     bekommen wie der Finger. */
  if (zu) return <Link to={zu} params={params as never} {...eigenschaften}>{inhalt}</Link>;
  if (beiKlick) {
    return (
      <button
        type="button"
        {...eigenschaften}
        {...(gedrueckt === undefined ? {} : { "aria-pressed": gedrueckt })}
        onClick={beiKlick}
      >
        {inhalt}
      </button>
    );
  }
  return <div {...eigenschaften}>{inhalt}</div>;
}

/* ══════════════ 5 · Der Leersatz ══════════════

   **Ein Leerzustand ist ein Satz auf einem Blatt, kein leerer Kasten.** Der
   Quelltext des Vorbilds begründet das so gut, dass es hier wörtlich steht:
   *„Ein Satz auf blankem Grund unter einer Überschrift sieht aus, als fehle
   etwas. In einer Gruppe sieht er aus, als sei die Gruppe leer — und das ist
   die Wahrheit."*

   **Nehmen, wenn** ein Abschnitt nichts enthält — und zwar **anstelle** der
   Liste, im selben Abschnitt, unter derselben Überschrift.

   **Nicht nehmen für eine Fußnote** („Preise ohne Mehrwertsteuer") und nicht
   für einen Hinweis am Feld („Mindestens 3 Zeichen"). Beide stehen hinter
   etwas, nicht anstelle von etwas — ein Kasten darum wäre eine Gruppe um ein
   einzelnes Ding.

   **Der Satz sagt, was nicht da ist, und nicht, dass etwas fehlt.** „Heute
   ist nichts fällig." ist eine Auskunft; „Keine Daten" ist eine Diagnose. */
export function Leersatz({ children }: { children: ReactNode }) {
  return <p className="hf-leersatz">{children}</p>;
}

/* ══════════════ 6 · Die Kurzfassung ══════════════

   **Ein Satz in Prosa statt einer Feldtabelle**, mit einer Kante links und
   dem Wichtigen fett. Im Vorbild steht er ganz oben in der Kundenakte:
   *„Letzter Kontakt **heute**. Ein Vorgang offen. Ansprechpartner ist **Luca
   Sliwinski**."*

   **Warum das besser ist als drei Felder:** Drei Beschriftungen mit drei
   Werten daneben muss man lesen **und** zuordnen. Ein Satz ist schon
   zugeordnet. Und was nicht zutrifft, steht gar nicht erst da — eine
   Feldtabelle muss „—" zeigen, ein Satz lässt den Halbsatz weg.

   **Nehmen, wenn** ein Überblick mit einer Einschätzung beginnen soll: die
   Kundenakte, der Einsatz, der Vorgang.

   **Nicht nehmen für Werte, die verglichen oder gerechnet werden.** Eine
   Zahl, die in eine Rechnung eingeht, gehört in eine Zeile mit ihrer
   Beschriftung und in Mono — in einem Satz kann man sie nicht untereinander
   lesen.

   **Der Satz wird gebaut und nicht zusammengesetzt.** Wer ihn aus Bruchstücken
   klebt („Letzter Kontakt " + wert + "."), hat einen i18n-Schlüssel je
   Bruchstück und eine Sprache, die sich nicht übersetzen lässt. Ein Schlüssel,
   ein Satz, Platzhalter darin. */
export function Kurzfassung({ children }: { children: ReactNode }) {
  return <p className="hf-kurzfassung">{children}</p>;
}

/* ══════════════ 7 · Die Griffreihe ══════════════

   **Drei breite, flache Symbolknöpfe nebeneinander** — im Vorbild unter dem
   Kundennamen: anrufen, Route, schreiben. Kein Text, nur das Zeichen.

   **Nehmen, wenn** es zwei bis vier Handlungen gibt, die **alle gleich
   wahrscheinlich** sind und **aus dem Schirm hinausführen** — telefonieren,
   navigieren, schreiben. Genau dafür ist die Reihe da.

   **Nicht nehmen für die Hauptaktion der Maske.** Die ist eine, sie trägt ein
   Wort, und sie steht in Hausgelb im Kopf (28-designkonzept.md: „eine
   Hauptaktion je Maske. Die gelbe Fläche kommt genau einmal vor"). Eine
   Griffreihe, in der ein Knopf wichtiger ist als die anderen, ist keine
   Reihe, sondern eine Verlegenheit.

   **Nie mehr als vier**, und nie ohne `wort`: Es steht in `aria-label` und
   `title`, auch wenn niemand es sieht. */
export function Griffreihe({ children }: { children: ReactNode }) {
  return <div className="hf-griffreihe">{children}</div>;
}

/** Ein Griff in der Reihe. `zu` für einen Weg, `href` für `tel:` und `mailto:`. */
export function Griff({ zeichen, wort, zu, href, beiKlick }: {
  zeichen: Zeichenname;
  /** Das Wort dazu — nicht gezeichnet, aber für Vorleser und Maus da. */
  wort: string;
  zu?: string;
  href?: string;
  beiKlick?: () => void;
}) {
  const inhalt = <Zeichen name={zeichen} />;
  if (zu) return <Link to={zu} className="hf-griff" aria-label={wort} title={wort}>{inhalt}</Link>;
  if (href) return <a href={href} className="hf-griff" aria-label={wort} title={wort}>{inhalt}</a>;
  return (
    <button type="button" className="hf-griff" aria-label={wort} title={wort} onClick={beiKlick}>
      {inhalt}
    </button>
  );
}

/* ══════════════ 8 · Die Wertzeile ══════════════

   *Aufgenommen am Abend des 18.09.2026. Gebaut hat sie der Vorgang
   (`masken/vorgaenge/Vorgang.tsx`, `.vg-wertzeile`); die Maße sind von dort
   unverändert übernommen, damit beim Umziehen nichts springt.*

   **Beschriftung links, Wert rechts, 40 px.** Kein Punkt, kein Winkel, kein
   Ziel — „GEGENSTAND · Radlader 5 t", „ZEITRAUM · 01.05. – 03.05.".

   **Sie gehört hierher, weil dieser Baustein sie selbst verlangt hat:** Am
   `Zweizeiler` steht seit dem ersten Tag *„Nicht nehmen für ein Wertepaar …
   das steht in einem `Zeilenblatt` als schlichte Zeile"* — und diese Zeile
   gab es dann nicht. Wer eine Form verbietet, schuldet die Alternative,
   sonst baut sie die nächste Maske selbst.

   **Und sie ist kleiner, nicht nur anders:** Vier Wertepaare sind als
   Wertzeilen 160 px hoch, als `Zweizeiler` 224. Ein Wertepaar hat keine
   zweite Zeile, keine Ampel und kein Ziel; es so zu zeichnen, als hätte es
   das, kostet 64 px und verspricht dreierlei, das nicht da ist.

   **Nehmen, wenn** ein Block aus Beschriftung und Wert besteht — Kopfdaten
   eines Vorgangs, Stammdaten eines Geräts, Zähler in einem Protokoll.

   **Nicht nehmen, wenn** die Zeile etwas öffnet (dann `Zweizeiler`) und
   nicht, wenn der Wert ein Satz ist (dann `Kurzfassung`): Ein Satz
   rechtsbündig neben einer Beschriftung liest sich zweimal falsch herum.

   **Der Wert steht rechts und in `tabular-nums`** — untereinander sind es
   Zahlen, die verglichen werden, und die tanzen sonst.

   ── Ein Betrag steht in Mono (19.09.2026) ─────────────────────────

   `betrag` setzt den Wert in JetBrains Mono, wie jeden Betrag im Haus
   (28-designkonzept.md, „Alles, was verglichen wird"); `summe` dazu fett —
   die Zahl, die am Telefon genannt wird. Nachgezogen für die Auskunft im
   Nachschlagen, deren Rechnung sonst die einzige Stelle gewesen wäre, an
   der „2.000,00" in der Schrift eines Satzes steht. */
export function Wertzeile({ was, betrag = false, summe = false, children }: {
  /** Die Beschriftung. Kurz und in Versalien — ein Satz ist keine Beschriftung. */
  was: ReactNode;
  /** Der Wert ist ein Betrag — Mono, rechtsbündig. */
  betrag?: boolean;
  /** Der Betrag ist die Summe darüber — fett, wie am Fuß jeder Rechnung. */
  summe?: boolean;
  children: ReactNode;
}) {
  const art = summe ? "summe" : betrag ? "betrag" : undefined;
  return (
    <div className="hf-wertzeile" {...(art ? { "data-art": art } : {})}>
      <span className="hf-wertzeile-was">{was}</span>
      <span className="hf-wertzeile-wert">{children}</span>
    </div>
  );
}

/* ══════════════ 9 · Was nicht uns gehört ══════════════

   *Aufgenommen am Abend des 18.09.2026. Gebaut hat es der Vorgang
   (`.vg-fremd`); Muster und Maße sind von dort unverändert übernommen.*

   **Eine diagonal schraffierte Fläche mit einer Umriss-Pille, die die Quelle
   nennt.** Aus dem Vorbild (42-vorbild-ovis.md): Dort liegen Outlook-Termine
   so, und die Pille sagt „Outlook".

   **Bei uns heißt die Quelle Lexware Office** — und damit ist dies die
   sichtbare Form von **Regel 1**: Faktura, Nummernkreis, E-Rechnung, Mahnwesen
   und Archiv bleiben drüben. Wer einen Beleg auf der Schraffur sieht, sieht,
   dass MrMachine ihn nicht geschrieben hat.

   **Die Schraffur ist keine neue Form**, sondern die, die das Brett für
   `wartung`, `transport` und `sperre` führt (45°, 5 px auf 5 px). Sie sagte
   dort „gehört nicht zur Vermietung" und sagt hier „gehört nicht uns" — und
   sie braucht dafür keine zweite Farbe.

   **Die Zeilen darin bleiben Papier.** Sie liegen **auf** der Schraffur, nicht
   darin: Eine Belegnummer auf einem Muster wäre schlechter zu lesen als jede
   andere Nummer im Haus.

   **Nehmen, wenn** gezeigt wird, was **drüben** steht — Rechnungen, Belege,
   Artikel, Zahlungen.

   **Nicht nehmen für unsere eigenen Daten, die nur dorthin zeigen.** Eine
   Artikelnummer, die wir am Preis führen, ist unsere; sie wird nicht
   schraffiert, weil sie hier gepflegt wird. Sonst ist am Ende die halbe
   Anwendung schraffiert, und das Muster sagt nichts mehr. */
export function Fremd({ quelle, children }: {
  /** Der Name der Quelle, wie er in der Pille steht — „Lexware Office". */
  quelle: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="hf-fremd">
      <span className="hf-fremd-quelle">{quelle}</span>
      {children}
    </div>
  );
}

/* ══════════════ 10 · Der Weiterweg ══════════════

   **Der Abschluss eines kurzen Überblicks:** „Volle Akte öffnen ›", und
   darunter leise, was dort steht — *„Adressen, Dokumente, Mails und Notizen
   stehen dort."*

   **Das ist der eigentliche Gedanke des Vorbilds**, und er gilt bei uns
   genauso: Ein Überblick, der alles zeigt, ist keiner. Er zeigt wenig und
   sagt am Ende, wo der Rest liegt. Luca hat am 18.09.2026 für unsere
   Kundenakte dasselbe verlangt — Personen und Einsatzorte raus aus dem
   Überblick.

   **Nehmen, wenn** ein Schirm bewusst kurz ist und es eine vollständige
   Fassung gibt.

   **Nicht nehmen als Navigationspunkt.** Er steht **ganz unten** und genau
   einmal; zwei davon untereinander heißen, dass der Schirm nicht weiß, wofür
   er da ist.

   **Der Satz darunter ist Pflicht.** Ohne ihn ist es ein Knopf, der „mehr"
   verspricht — mit ihm ist es eine Auskunft, nach der man entscheiden kann,
   ob man tippt. */
export function Weiterweg({ zu, params, beiKlick, children, satz }: {
  /** Eine Adresse. `/kunden/$nr` braucht dazu `params`. */
  zu?: string;
  params?: Record<string, string>;
  /** Statt einer Adresse: ein Reiter, ein Blatt — siehe „Ein Ziel ist nicht immer eine Adresse". */
  beiKlick?: () => void;
  children: ReactNode;
  /** Was dort steht. Ein Satz, kein Wort. */
  satz: ReactNode;
}) {
  return (
    <div className="hf-weiterweg">
      <Ziel weg={zu} params={params} beiKlick={beiKlick} klasse="hf-weiterweg-knopf">
        <b>{children}</b>
        <Zeichen name="weiter" />
      </Ziel>
      <p className="hf-weiterweg-satz">{satz}</p>
    </div>
  );
}

/* ══════════════ 11 · Das Suchfeld (19.09.2026) ══════════════

   Luca, am selben Tag über alle Telefonschirme: *„…das muss einheitlich sein
   von der Struktur, sodass die Buttons sauber sind."*

   **Es gab dieselbe Form viermal, in vier Klassen:** `.hsu-feld` (Suche),
   `.vmt-suche` (Vermieten), `.rt-suche` (Ressourcen), `.pt-suche`
   (Preisstamm) — und das Nachschlagen hatte eine fünfte, ein graues Feld
   ohne Lupe (`.hn-suche`). Jede Kopie trug in ihrem Kommentar, sie sei
   „dieselbe Form wie die Suche", und zwei davon waren es schon nicht mehr:
   Die Ressourcen hatten die leise Kante in `--linie` und 9 px Rundung, das
   Nachschlagen 56 px Höhe und 17 px Schrift.

   > **Ein Feld, das auf jedem Schirm anders aussieht, liest man jedes Mal
   > neu.** Also ist es ein Baustein, und die Kopien sind weg.

   ── Die Gestalt ist die der Suche ─────────────────────────────────

   Die Suche war die erste und ist dem Vorbild nachgebaut (`SucheTelefon.tsx`):
   **die Lupe im Rahmen**, nicht daneben — sie ist kein Knopf, sondern sagt
   dem leeren Feld seinen Zweck an; **die Kante in Tinte**, 1,5 px, weil ein
   Suchfeld der Einstieg einer Fläche ist und nicht ein Feld unter vielen;
   **48 px hoch und 16 px Schrift** — darunter trifft der Daumen schlechter,
   und darunter fährt Safari beim Antippen an das Feld heran und lässt die
   Seite vergrößert stehen. Die Ressourcen hatten die leise Kante mit Absicht
   („ein Werkzeug über einer Liste"); zwei Kanten für dieselbe Geste sind
   trotzdem zwei Formen, und genau das wollte Luca nicht.

   **Das eigene Kreuz statt des Browserkreuzes.** Das von Safari ist 12 px
   groß, das von Chrome blau — keins unseres und keins, das ein Daumen
   trifft. Es leert **und gibt den Fokus zurück**: Wer löscht, will neu
   tippen.

   **„Suchen" auf der Tastatur schließt sie** (`enterKeyHint="search"`). Die
   Treffer stehen schon beim Tippen da; die Taste heißt hier „ich bin
   fertig, zeig mir die Liste", und die Tastatur deckt sie sonst zur Hälfte
   zu.

   **Nehmen, wenn** eine Fläche am Telefon nach einem Wort filtert.
   **Nicht nehmen für ein Formularfeld** („Name", „Betrag") — das ist ein
   Feld mit Schild darüber und kein Einstieg.

   **Kein Erstfokus von selbst.** Die Tastatur beim Betreten deckt die halbe
   Fläche zu (40-mobiles-grundgeruest.md, „Kein Blatt fokussiert beim Öffnen
   ein Eingabefeld"). Wer ihn will — die Suche, über ihr Fach geöffnet —,
   reicht `ref` durch und setzt ihn selbst. */
export function Suchfeld({ wert, aendern, platz, schild, ref }: {
  wert: string;
  /** Mit dem neuen Wort — beim Leeren mit `""`. Wer dabei mehr zurücksetzt, tut es hier. */
  aendern: (neu: string) => void;
  /** Was im leeren Feld steht — was man hier findet, nicht „Suchen …". */
  platz: string;
  /** Der Name für Vorleser. Das Feld hat kein sichtbares Schild; die Lupe ist keins. */
  schild: string;
  /** Das Eingabefeld — für den, der den Fokus selbst setzen will. */
  ref?: Ref<HTMLInputElement>;
}) {
  const t = useT();
  const feld = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => feld.current as HTMLInputElement, []);

  return (
    <label className="hf-suchfeld">
      <Zeichen name="suche" />
      <input
        ref={feld}
        type="search"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        aria-label={schild}
        placeholder={platz}
        value={wert}
        onChange={(e) => aendern(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      />
      {wert ? (
        <button
          type="button"
          className="hf-suchfeld-leeren"
          aria-label={t("allgemein.suchwortLoeschen")}
          title={t("allgemein.suchwortLoeschen")}
          onClick={() => { aendern(""); feld.current?.focus(); }}
        >
          <Zeichen name="kreuz" />
        </button>
      ) : null}
    </label>
  );
}
/* ══════════════ 12 · Die Teile einer Listenzeile ══════════════

   *Aufgenommen am 19.09.2026, als Start und Auftrag am selben Tag dieselbe
   Zeile bauten.* Luca: *„Das muss einheitlich sein von der Struktur, sodass
   die Buttons sauber sind und diese Einträge in der Liste immer gleich hoch
   sind, damit es ein konsistentes Design einfach ist."*

   Drei Kleinigkeiten, die in jeder Liste wiederkehren — und die zweimal
   gebaut zweimal anders aussehen:

   | | wofür | |
   |---|---|---|
   | `Unterzeile` | die zweite Zeile: **die Art in Tinte**, dahinter der Zusammenhang | war vorher eine Marke |
   | `Wann` | die Zeit rechts, **zweistöckig**: oben der Wert in Mono, darunter leise „heute", „seit" | wie auf „Heute" |
   | `Handlungen` | die Knöpfe unten im Blatt: untereinander, gleich breit, **eine** in Hausgelb | keine Knöpfe in der Zeile |

   Sie stehen hier und nicht in einer Maske, weil sie in dreien vorkommen:
   Start (`masken/handy/Startzeilen.tsx`), Auftrag
   (`masken/vorgaenge/Vorgang.tsx`) und alles, was danach kommt. */

/** Die Art vorn in der zweiten Zeile, dahinter der Zusammenhang. */
export function Unterzeile({ art, dazu }: { art: ReactNode; dazu?: string }) {
  return (
    <>
      <span className="hf-art">{art}</span>
      {dazu ? ` · ${dazu}` : ""}
    </>
  );
}

/** Eine Kennung in Mono — sie wird verglichen, nicht gelesen. */
export function Kennung({ children }: { children: ReactNode }) {
  return <span className="hf-kennung">{children}</span>;
}

/**
 * Die Zeit rechts in einer Zeile — zweistöckig: oben der Wert, darunter
 * leise, wovon er handelt („heute 07:00" wird zu `07:00` über `heute`).
 */
export function Wann({ text }: { text: string }) {
  if (!text) return null;
  const teile = text.split(" ");
  let ab = teile.length - 1;
  while (ab > 0 && !/\d/.test(teile[ab] ?? "")) ab -= 1;
  let oben = teile.slice(ab).join(" ");
  let unten = teile.slice(0, ab).join(" ");
  /* „seit 15 Tagen" wurde zu „15 Tagen" über „seit" — die Präposition
     gehört zum Wert, nicht darunter (Signal, 07.10.2026). Dann steht der
     ganze Satz in einer Zeile. */
  if (/^(seit|ab|bis|vor|in|um|am|noch)$/i.test(unten)) { oben = text; unten = ""; }
  return (
    <span className="hf-wann">
      <b>{oben}</b>
      {unten ? <small>{unten}</small> : null}
    </span>
  );
}

/** Die Knöpfe unten in einem Blatt — eine Hauptaktion in Hausgelb, der Rest ruhig. */
export function Handlungen({ children }: { children: ReactNode }) {
  return <div className="hf-handlungen">{children}</div>;
}

/* ══════════════ Die Chipreihe (19.09.2026) ══════════════

   **Nehmen, wenn** eine Liste auf eine Sorte, einen Bereich oder eine
   Ansicht eingeschränkt wird und die Wahl **dieselbe Fläche** zeigt — Suche
   (Alles · Kunden · Aufträge …), Belege, Abrechnung, Weiteres, Preisstamm.
   Die Chips darin sind die des Hauses (`.chip` oder `.pille` mit
   `aria-pressed`, gewählt in Hausgelb).

   **Nicht nehmen für** Reiter einer Akte (die führen an verschiedene Orte,
   das ist die `Reiterleiste`) und für eine Wahl mit zwei festen Seiten (das
   ist ein Umschalter).

   Luca, 19.09.2026: *„Dann kannst du das mit den Chips auf jeden Fall in die
   Handy-App packen."* — bis dahin stand dieselbe Reihe fünfmal als Kopie in
   fünf Telefondateien (`.hsu-chips`, `.hbe-chips`, `.hab-chips`,
   `.hwt-chips`, `.pt-bereiche`), jede mit denselben Maßen und jede mit einer
   eigenen Chance, beim nächsten Nachmessen nicht mitgezogen zu werden.

   **Was sie kann:** Sie rollt über den Seitenrand hinaus und blendet rechts
   aus — das Zeichen, dass es weitergeht; 36 px zu sehen, 44 px zu treffen;
   und der gewählte Chip rückt von selbst ins Bild (`gewaehlt` ändert sich →
   er wird eingerollt). `Chiptrenner` teilt Gruppen in derselben Reihe. */
export function Chipreihe({ schild, gewaehlt, children }: {
  /** Was ein Vorleser sagt — „Auf eine Sorte einschränken". */
  schild: string;
  /** Die aktuelle Wahl; ändert sie sich, rollt der gewählte Chip ins Bild. */
  gewaehlt?: string | number | null;
  children: ReactNode;
}) {
  const reihe = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const chip = reihe.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    chip?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [gewaehlt]);
  return (
    <div className="hf-chipreihe" role="group" aria-label={schild} ref={reihe}>
      {children}
    </div>
  );
}

/** Ein Strich zwischen zwei Gruppen einer Chipreihe — Luft allein läse sich als Lücke. */
export function Chiptrenner() {
  return <span className="hf-chipreihe-trenn" aria-hidden="true" />;
}
